import { z } from 'zod';
import { emailSchema, idSchema, passwordSchema, schoolSlugSchema } from './common.js';
import { PERMISSIONS, ROLES, SCOPES } from '../permissions/index.js';
export const roleSchema = z.enum(ROLES);
export const permissionSchema = z.enum(PERMISSIONS);
export const scopeSchema = z.enum(SCOPES);
export const grantSchema = z.object({
    permission: permissionSchema,
    scope: scopeSchema,
});
/**
 * Emails are unique per school, not globally, so a login needs to name one.
 *
 * On `<school>.hamro.school` the hostname names it and this field is absent —
 * the sign-in screen does not show it. On the shared `app.hamro.school` the
 * user types it. The server takes the hostname over the body when both are
 * present: a request cannot talk its way into another tenant.
 */
export const loginRequestSchema = z.object({
    /**
     * `username@school-slug`, or bare `username` on a school's own subdomain.
     * Not an email address — see tenancy/identifier.ts.
     */
    identifier: z.string().trim().min(1, { message: 'validation.required' }).max(120),
    password: z.string().min(1, { message: 'validation.required' }),
});
export const sessionUserSchema = z.object({
    id: idSchema,
    identifier: z.string(),
    username: z.string(),
    /** A real mailbox, if the school recorded one. Nothing can be sent to the identifier. */
    contactEmail: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    locale: z.string(),
    roles: z.array(roleSchema),
    permissions: z.array(grantSchema),
    school: z.object({
        id: idSchema,
        slug: z.string(),
        name: z.string(),
        timezone: z.string(),
        currency: z.string(),
        currencyMinorUnits: z.number().int(),
        defaultLocale: z.string(),
    }),
});
/**
 * The refresh token is not in this body — it is set as an httpOnly cookie, out
 * of reach of any script on the page. The Flutter app, which has no cookie
 * jar worth the name, asks for it in the body instead via `client=mobile`.
 */
export const loginResponseSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int(),
    refreshToken: z.string().optional(),
    user: sessionUserSchema,
});
export const refreshRequestSchema = z.object({
    refreshToken: z.string().optional(),
});
export const refreshResponseSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int(),
    refreshToken: z.string().optional(),
});
export const meResponseSchema = z.object({ user: sessionUserSchema });
export const registerDeviceRequestSchema = z.object({
    token: z.string().min(10).max(4096),
    platform: z.enum(['ANDROID', 'IOS', 'WEB']),
    appVersion: z.string().max(40).optional(),
    deviceModel: z.string().max(120).optional(),
    locale: z.string().max(20).optional(),
});
export const changePasswordRequestSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
//# sourceMappingURL=auth.js.map